import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw, AlertTriangle, ShieldCheck, Radar } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { ForecastChart } from '@/components/explore/ForecastChart';

// Auto-refresh cadences (label → interval in ms). Remembered per viewer so a
// live dashboard keeps a pinned forecast current without manual clicks.
const CADENCES = [
  { v: 0, label: 'Auto-refresh: Off' },
  { v: 5 * 60 * 1000, label: 'Every 5 min' },
  { v: 15 * 60 * 1000, label: 'Every 15 min' },
  { v: 60 * 60 * 1000, label: 'Every hour' },
];

// Sentinel status → badge styling for the pinned-sentinel header strip.
const SENTINEL_META = {
  projected_issue: { label: 'Projected issue', cls: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30', Icon: AlertTriangle },
  ok: { label: 'Watching', cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20', Icon: ShieldCheck },
  low_confidence: { label: 'Low confidence', cls: 'bg-amber-500/10 text-amber-300 border-amber-500/20', Icon: Radar },
};
const CONF_CLS = { High: 'text-emerald-300', Medium: 'text-amber-300', Low: 'text-rose-300' };

// Renders a pinned forecast snapshot on a dashboard/report tile. The snapshot
// displays instantly; the Refresh button recomputes on demand and an optional
// auto-refresh cadence recomputes it periodically while the dashboard is open.
// When `sentinel` is present, a status strip shows the Forecast Sentinel's
// current confidence / projected-issue state alongside the chart.
export function ForecastTile({ snapshot, meta, formatValue, sentinel }) {
  const [fc, setFc] = useState(snapshot);
  const [refreshing, setRefreshing] = useState(false);
  const [renderKey, setRenderKey] = useState(0);

  const storageKey = `inzara.fc_autorefresh:${(meta?.model_id || snapshot?.model_id) || ''}:${(meta?.source_question || snapshot?.source_question) || ''}`;
  const [autoMs, setAutoMs] = useState(() => {
    const v = Number(typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : 0);
    return CADENCES.some(c => c.v === v) ? v : 0;
  });

  const refresh = async (silent = false) => {
    const m = meta || {};
    const modelId = m.model_id || fc?.model_id;
    if (!modelId) { if (!silent) toast.error('This forecast can\u2019t be refreshed.'); return; }
    setRefreshing(true);
    try {
      const res = await api.forecastRebuild({
        model_id: modelId,
        source_question: m.source_question || fc?.source_question,
        context_question: m.context_question || fc?.context_question,
        unit: fc?.unit || m.unit,
        horizon: fc?.horizon || m.horizon,
        top_n: (fc?.top_n ?? m.top_n) ?? null,
      });
      const nf = res?.result?.forecast;
      if (nf) { setFc(nf); setRenderKey(k => k + 1); if (!silent) toast.success('Forecast refreshed'); }
      else if (!silent) toast.error('Could not refresh the forecast');
    } catch (e) { if (!silent) toast.error(e.message || 'Could not refresh the forecast'); }
    finally { setRefreshing(false); }
  };

  // Keep a stable reference for the interval so it always calls the latest refresh.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    if (!autoMs) return undefined;
    const id = setInterval(() => refreshRef.current(true), autoMs);
    return () => clearInterval(id);
  }, [autoMs]);

  const onCadence = (v) => {
    const ms = Number(v);
    setAutoMs(ms);
    try { window.localStorage.setItem(storageKey, String(ms)); } catch { /* ignore */ }
    const c = CADENCES.find(x => x.v === ms);
    toast.success(ms ? `Auto-refresh on — ${c?.label.toLowerCase()}` : 'Auto-refresh off');
  };

  if (!fc) return null;
  const sm = sentinel && (SENTINEL_META[sentinel.status] || SENTINEL_META.ok);
  return (
    <div className="h-full w-full flex flex-col" data-testid="forecast-tile">
      {sentinel && sm && (
        <div className={`flex items-center gap-1.5 mb-1 px-2 py-1 rounded-lg border text-[10px] ${sm.cls}`} data-testid="forecast-tile-sentinel">
          <sm.Icon className="w-3 h-3" />
          <span className="font-medium">{sm.label}</span>
          {sentinel.confidence && <span className={`${CONF_CLS[sentinel.confidence] || ''}`}>· {sentinel.confidence} confidence</span>}
          {sentinel.model && <span className="text-muted-foreground">· {sentinel.model}</span>}
          {sentinel.reason && <span className="text-muted-foreground truncate">· {sentinel.reason}</span>}
        </div>
      )}
      <div className="flex items-center justify-end gap-1.5 mb-1 print:hidden">
        <select
          value={autoMs}
          onChange={(e) => onCadence(e.target.value)}
          data-testid="forecast-tile-autorefresh"
          title="Automatically recompute this forecast on a schedule while the dashboard is open"
          className="text-[10px] bg-background/60 border border-border/40 rounded px-1.5 py-0.5 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
        >
          {CADENCES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
        </select>
        <button
          onClick={() => refresh(false)}
          disabled={refreshing}
          data-testid="forecast-tile-refresh"
          className="flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 rounded px-1.5 py-0.5 disabled:opacity-50"
          title="Recompute this forecast now"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Refreshing\u2026' : 'Refresh'}
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        <ForecastChart key={renderKey} forecast={fc} formatValue={formatValue} />
      </div>
    </div>
  );
}
